<template>
  <v-container class="py-6 px-4" style="max-width: 1100px;">
    <!-- Título da Página -->
    <div class="mb-6">
      <div class="d-flex align-center">
        <v-icon color="#315fd8" size="32" class="mr-2">mdi-chart-timeline-variant-shimmer</v-icon>
        <h1 class="text-h4 font-weight-bold" style="color: #0f172a;">Métricas Flashcards 2.0</h1>
      </div>
      <p class="text-body-2 text-medium-emphasis mt-1 mb-0">
        Acompanhamento de retenção, repetição espaçada SM-2 e cobertura pedagógica das provas TEMFC.
      </p>
    </div>

    <!-- Cards Resumo no Topo -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="metric-summary-card pa-4 elevation-1">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-caption font-weight-bold text-uppercase" style="color: #64748b;">Total Estudados</span>
            <v-avatar size="32" color="rgba(49, 95, 216, 0.1)">
              <v-icon size="18" color="#315fd8">mdi-cards</v-icon>
            </v-avatar>
          </div>
          <div class="text-h4 font-weight-bold" style="color: #0f172a;">{{ totalCardsV2 }}</div>
          <div class="text-caption text-medium-emphasis mt-1">revisões realizadas</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="metric-summary-card pa-4 elevation-1">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-caption font-weight-bold text-uppercase" style="color: #64748b;">Estudados Hoje</span>
            <v-avatar size="32" color="rgba(16, 185, 129, 0.1)">
              <v-icon size="18" color="#10b981">mdi-calendar-check</v-icon>
            </v-avatar>
          </div>
          <div class="text-h4 font-weight-bold" style="color: #10b981;">{{ totalCardsToday }}</div>
          <div class="text-caption text-medium-emphasis mt-1">cards no dia de hoje</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="metric-summary-card pa-4 elevation-1">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-caption font-weight-bold text-uppercase" style="color: #64748b;">Devidos Hoje (SM-2)</span>
            <v-avatar size="32" :color="dueCardsTodayCount > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(100, 116, 139, 0.1)'">
              <v-icon size="18" :color="dueCardsTodayCount > 0 ? '#ef4444' : '#64748b'">mdi-clock-alert-outline</v-icon>
            </v-avatar>
          </div>
          <div class="text-h4 font-weight-bold" :style="{ color: dueCardsTodayCount > 0 ? '#ef4444' : '#0f172a' }">
            {{ dueCardsTodayCount }}
          </div>
          <div class="text-caption text-medium-emphasis mt-1">aguardando revisão</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="metric-summary-card pa-4 elevation-1">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-caption font-weight-bold text-uppercase" style="color: #64748b;">Cobertura da Matriz</span>
            <v-avatar size="32" color="rgba(245, 158, 11, 0.1)">
              <v-icon size="18" color="#f59e0b">mdi-bullseye-arrow</v-icon>
            </v-avatar>
          </div>
          <div class="text-h4 font-weight-bold" style="color: #d97706;">
            {{ coveragePercentage }}%
          </div>
          <div class="text-caption text-medium-emphasis mt-1">
            {{ uniqueCardsLearned }} de 1.752 cards
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gráfico de Evolução Diária -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="pa-4 elevation-1 rounded-xl">
          <div class="d-flex align-center justify-space-between mb-2">
            <div>
              <div class="text-h6 font-weight-bold" style="color: #0f172a;">Evolução de Cards Estudados / Dia</div>
              <div class="text-caption text-medium-emphasis">Histórico de revisões acumuladas ao longo do tempo</div>
            </div>
            <v-chip size="small" color="primary" variant="tonal" class="font-weight-bold">
              Flashcards 2.0
            </v-chip>
          </div>
          <div id="chart-v2" ref="chartRef" class="chart-container"></div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Cobertura Pedagógica por Bloco e Ciclo -->
    <v-row class="mb-4">
      <v-col cols="12" md="7">
        <v-card class="pa-5 elevation-1 rounded-xl h-100">
          <div class="text-h6 font-weight-bold mb-1" style="color: #0f172a;">
            Progresso por Bloco & Ciclo
          </div>
          <div class="text-caption text-medium-emphasis mb-4">
            Distribuição dos 1.752 cards categorizados por área pedagógica
          </div>

          <!-- Conceitos Importantes -->
          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-1 text-body-2 font-weight-medium">
              <span class="d-flex align-center">
                <v-icon size="16" color="#315fd8" class="mr-1">mdi-brain</v-icon>
                Conceitos importantes
              </span>
              <span class="text-caption font-weight-bold">{{ blockProgress.conceitos.count }} / 595</span>
            </div>
            <v-progress-linear
              :model-value="(blockProgress.conceitos.count / 595) * 100"
              color="#315fd8"
              height="8"
              rounded
              bg-color="#e2e8f0"
            ></v-progress-linear>
          </div>

          <!-- Clínicos: Pediatria -->
          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-1 text-body-2 font-weight-medium">
              <span class="d-flex align-center">
                <v-icon size="16" color="#0284c7" class="mr-1">mdi-baby-carriage</v-icon>
                Clínicos › Pediatria
              </span>
              <span class="text-caption font-weight-bold">{{ blockProgress.pediatria.count }} / 122</span>
            </div>
            <v-progress-linear
              :model-value="(blockProgress.pediatria.count / 122) * 100"
              color="#0284c7"
              height="8"
              rounded
              bg-color="#e2e8f0"
            ></v-progress-linear>
          </div>

          <!-- Clínicos: GO -->
          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-1 text-body-2 font-weight-medium">
              <span class="d-flex align-center">
                <v-icon size="16" color="#ec4899" class="mr-1">mdi-human-pregnant</v-icon>
                Clínicos › Ginecologia e Obstetrícia
              </span>
              <span class="text-caption font-weight-bold">{{ blockProgress.go.count }} / 128</span>
            </div>
            <v-progress-linear
              :model-value="(blockProgress.go.count / 128) * 100"
              color="#ec4899"
              height="8"
              rounded
              bg-color="#e2e8f0"
            ></v-progress-linear>
          </div>

          <!-- Clínicos: Adulto -->
          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-1 text-body-2 font-weight-medium">
              <span class="d-flex align-center">
                <v-icon size="16" color="#10b981" class="mr-1">mdi-account</v-icon>
                Clínicos › Adulto
              </span>
              <span class="text-caption font-weight-bold">{{ blockProgress.adulto.count }} / 830</span>
            </div>
            <v-progress-linear
              :model-value="(blockProgress.adulto.count / 830) * 100"
              color="#10b981"
              height="8"
              rounded
              bg-color="#e2e8f0"
            ></v-progress-linear>
          </div>

          <!-- Clínicos: Idoso -->
          <div class="mb-2">
            <div class="d-flex justify-space-between align-center mb-1 text-body-2 font-weight-medium">
              <span class="d-flex align-center">
                <v-icon size="16" color="#8b5cf6" class="mr-1">mdi-account-cane</v-icon>
                Clínicos › Idoso
              </span>
              <span class="text-caption font-weight-bold">{{ blockProgress.idoso.count }} / 77</span>
            </div>
            <v-progress-linear
              :model-value="(blockProgress.idoso.count / 77) * 100"
              color="#8b5cf6"
              height="8"
              rounded
              bg-color="#e2e8f0"
            ></v-progress-linear>
          </div>
        </v-card>
      </v-col>

      <!-- Distribuição por Tipo e Retenção SM-2 -->
      <v-col cols="12" md="5">
        <v-card class="pa-5 elevation-1 rounded-xl h-100">
          <div class="text-h6 font-weight-bold mb-1" style="color: #0f172a;">
            Distribuição por Formato
          </div>
          <div class="text-caption text-medium-emphasis mb-4">
            Variedade dos 1.752 cards ativos na versão 2.0
          </div>

          <div class="d-flex flex-column gap-3">
            <div class="d-flex align-center justify-space-between pa-3 rounded-lg" style="background: #f8fafc; border: 1px solid #e2e8f0;">
              <div class="d-flex align-center">
                <v-avatar size="28" color="rgba(49, 95, 216, 0.1)" class="mr-2">
                  <v-icon size="16" color="#315fd8">mdi-card-text-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="text-body-2 font-weight-bold" style="color: #1e293b;">Básico (Pergunta / Resposta)</div>
                  <div class="text-caption text-medium-emphasis">Conceitos diretos e objetivos</div>
                </div>
              </div>
              <div class="text-right">
                <span class="text-subtitle-2 font-weight-bold">{{ formatDistribution.basic.count }}</span>
                <div class="text-caption text-medium-emphasis">{{ formatDistribution.basic.pct }}% ({{ studiedByType.basico }} estudados)</div>
              </div>
            </div>

            <div class="d-flex align-center justify-space-between pa-3 rounded-lg mt-3" style="background: #f8fafc; border: 1px solid #e2e8f0;">
              <div class="d-flex align-center">
                <v-avatar size="28" color="rgba(124, 58, 237, 0.1)" class="mr-2">
                  <v-icon size="16" color="#7c3aed">mdi-form-textbox</v-icon>
                </v-avatar>
                <div>
                  <div class="text-body-2 font-weight-bold" style="color: #1e293b;">Cloze (Completar Lacuna)</div>
                  <div class="text-caption text-medium-emphasis">Fixação de doses, critérios e valores</div>
                </div>
              </div>
              <div class="text-right">
                <span class="text-subtitle-2 font-weight-bold">{{ formatDistribution.cloze.count }}</span>
                <div class="text-caption text-medium-emphasis">{{ formatDistribution.cloze.pct }}% ({{ studiedByType.cloze }} estudados)</div>
              </div>
            </div>

            <div class="d-flex align-center justify-space-between pa-3 rounded-lg mt-3" style="background: #f8fafc; border: 1px solid #e2e8f0;">
              <div class="d-flex align-center">
                <v-avatar size="28" color="rgba(13, 148, 136, 0.1)" class="mr-2">
                  <v-icon size="16" color="#0d9488">mdi-clipboard-text-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="text-body-2 font-weight-bold" style="color: #1e293b;">Mini-caso Clínico</div>
                  <div class="text-caption text-medium-emphasis">Aplicação em cenários de APS</div>
                </div>
              </div>
              <div class="text-right">
                <span class="text-subtitle-2 font-weight-bold">{{ formatDistribution.mini.count }}</span>
                <div class="text-caption text-medium-emphasis">{{ formatDistribution.mini.pct }}% ({{ studiedByType.mini_caso }} estudados)</div>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import * as echarts from 'echarts';
import manifesto from '@/data/manifesto-decks.json';
import { isCardDue, getTodayDateString } from '@/utils/sm2';

export default {
  name: 'ResultsV2',
  data() {
    return {
      chartInstance: null,
    };
  },
  computed: {
    userData() {
      const users = this.$store.getters.userName;
      return (Array.isArray(users) && users.length > 0) ? users[0] : {};
    },
    totalCardsV2() {
      return this.userData.totalCardsV2 || this.userData.totalCards || 0;
    },
    trackingV2() {
      return this.userData.difficultyTrackingV2 || [];
    },
    totalCardsToday() {
      const currentDate = new Date();
      const day = currentDate.getDate();
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const todayStr = `${day}/${month}/${year}`;

      const dates = this.userData.datesV2 || this.userData.dates || [];
      const match = dates.find(d => d.date === todayStr);
      return match ? match.cards : 0;
    },
    dueCardsTodayCount() {
      const today = getTodayDateString();
      const uniqueDue = new Set();
      this.trackingV2.forEach(item => {
        const id = item.card_id || item.refId;
        if (id && !uniqueDue.has(id) && isCardDue(item, today)) {
          uniqueDue.add(id);
        }
      });
      return uniqueDue.size;
    },
    uniqueCardsLearned() {
      const uniqueIds = new Set();
      this.trackingV2.forEach(item => {
        const id = item.card_id || item.refId;
        if (id) uniqueIds.add(id);
      });
      return uniqueIds.size;
    },
    coveragePercentage() {
      const total = manifesto.n_cards_ativos || 1752;
      if (!total) return '0.0';
      const pct = Math.min(100, (this.uniqueCardsLearned / total) * 100);
      return pct.toFixed(1);
    },
    formatDistribution() {
      let basic = 0;
      let cloze = 0;
      let mini = 0;
      manifesto.decks.forEach(d => {
        if (d.nivel === 'subtema' && d.n_por_tipo) {
          basic += d.n_por_tipo.basico || 0;
          cloze += d.n_por_tipo.cloze || 0;
          mini += d.n_por_tipo.mini_caso || 0;
        }
      });
      const total = basic + cloze + mini || 1752;
      return {
        basic: { count: basic, pct: ((basic / total) * 100).toFixed(1) },
        cloze: { count: cloze, pct: ((cloze / total) * 100).toFixed(1) },
        mini: { count: mini, pct: ((mini / total) * 100).toFixed(1) },
      };
    },
    studiedByType() {
      const counts = { basico: 0, cloze: 0, mini_caso: 0 };
      const counted = new Set();
      this.trackingV2.forEach(item => {
        const id = item.card_id || item.refId;
        if (id && !counted.has(id)) {
          counted.add(id);
          const t = item.tipo || 'basico';
          if (counts[t] !== undefined) counts[t]++;
          else counts.basico++;
        }
      });
      return counts;
    },
    // Progresso por blocos/ciclos com base no histórico do aluno
    blockProgress() {
      const progress = {
        conceitos: { count: 0 },
        pediatria: { count: 0 },
        go: { count: 0 },
        adulto: { count: 0 },
        idoso: { count: 0 },
      };

      // Mapeia deck_id para bloco/ciclo
      const deckBlockMap = {};
      manifesto.decks.forEach(deck => {
        if (deck.nivel === 'subtema' && deck.caminho) {
          if (deck.caminho.startsWith('Conceitos importantes')) {
            deckBlockMap[deck.deck_id] = 'conceitos';
          } else if (deck.caminho.includes('Pediatria')) {
            deckBlockMap[deck.deck_id] = 'pediatria';
          } else if (deck.caminho.includes('Ginecologia e Obstetrícia')) {
            deckBlockMap[deck.deck_id] = 'go';
          } else if (deck.caminho.includes('Adulto')) {
            deckBlockMap[deck.deck_id] = 'adulto';
          } else if (deck.caminho.includes('Idoso')) {
            deckBlockMap[deck.deck_id] = 'idoso';
          }
        }
      });

      const countedCards = new Set();
      this.trackingV2.forEach(item => {
        const id = item.card_id || item.refId;
        if (!countedCards.has(id)) {
          countedCards.add(id);
          const category = deckBlockMap[item.deck_id];
          if (category && progress[category]) {
            progress[category].count += 1;
          }
        }
      });

      return progress;
    },
    chartData() {
      const dates = this.userData.datesV2 || this.userData.dates || [];
      const countMap = new Map();
      dates.forEach(item => {
        if (item && item.date) {
          countMap.set(item.date, Number(item.cards) || 0);
        }
      });

      const now = new Date();
      const result = [];
      // Janela móvel estrita contínua dos últimos 14 dias (D-13 até hoje)
      for (let i = 13; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
        const day = d.getDate();
        const month = d.getMonth() + 1;
        const year = d.getFullYear();
        const key = `${day}/${month}/${year}`;
        const label = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
        result.push({
          date: key,
          label,
          cards: countMap.get(key) || 0,
        });
      }
      return result;
    },
  },
  watch: {
    chartData: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
  },
  mounted() {
    this.renderChart();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  },
  methods: {
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    },
    updateChart() {
      if (!this.chartInstance) {
        this.renderChart();
        return;
      }
      const labels = this.chartData.map(item => item.label || item.date);
      const values = this.chartData.map(item => item.cards);
      this.chartInstance.setOption({
        xAxis: { data: labels },
        series: [{ data: values }],
      });
    },
    renderChart() {
      const chartElement = this.$refs.chartRef || document.getElementById('chart-v2');
      if (!chartElement) return;

      if (!this.chartInstance) {
        this.chartInstance = echarts.init(chartElement);
      }

      const labels = this.chartData.map(item => item.label || item.date);
      const values = this.chartData.map(item => item.cards);

      const options = {
        tooltip: {
          trigger: 'axis',
          formatter: '{b}<br/>Cards Estudados: <b>{c}</b>',
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '8%',
          top: '12%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: labels,
          axisLine: { lineStyle: { color: '#cbd5e1' } },
          axisLabel: { color: '#64748b' },
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: '#f1f5f9' } },
          axisLabel: { color: '#64748b' },
        },
        series: [
          {
            name: 'Cards Estudados',
            type: 'line',
            smooth: true,
            data: values,
            itemStyle: { color: '#315fd8' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(49, 95, 216, 0.28)' },
                { offset: 1, color: 'rgba(49, 95, 216, 0.02)' },
              ]),
            },
          },
        ],
      };

      this.chartInstance.setOption(options);
    },
  },
};
</script>

<style scoped>
.metric-summary-card {
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.chart-container {
  width: 100%;
  height: 320px;
}
</style>
